// Auth: the access gate, role gates, guaranteed session teardown, and the
// inactivity auto-logout. Ported from legacy v20_Index.html:
//   handleAccess          (~line 1146)  — access gate + retry-vs-deny branches
//   logoutApp             (~line 1257)  — sign-out with sb-* fallback sweep
//   showIdleWarning/hideIdleWarning/resetIdleTimer/startIdleTimer (~1262-1311)
// This module is the SINGLE source of role-gate truth (isAdmin/canAssign) —
// no view may re-implement the regexes.

import { store, SLICES } from './state/store.js';
import { getClient } from './db/client.js';
import { TABLES } from './db/contract.js';
import { register } from './ui/actions.js';
import { toast } from './ui/toast.js';
import { announce, cancelAnnounce } from './ui/announce.js';

// ─── Role gates (legacy lines 1174, 4510) ──────────────────────────────────

/**
 * Admin-tier UI (legacy `.admin-only`): admin and programme-manager roles.
 * Exact legacy regex — matches by substring, case-insensitive.
 * @param {string|null|undefined} role app_users.role
 * @returns {boolean}
 */
export function isAdmin(role) {
  return /admin|programmes/i.test(role || '');
}

/**
 * Assignment powers (bulk assign, "Assign to…") for admin/programmes AND
 * coordinators. Deliberately WIDER than isAdmin — coordinators can assign
 * patients without full admin access (legacy canAssign, line 4510).
 * @param {string|null|undefined} role app_users.role
 * @returns {boolean}
 */
export function canAssign(role) {
  return /admin|programmes|coordinator/i.test(role || '');
}

// ─── Access classification (the LOAD-BEARING branch decision) ──────────────

export const ACCESS_DENIED_MSG = 'Access denied. Contact your administrator.';

/**
 * Classify the app_users verification read. Pure — this encodes the safety
 * rule from legacy handleAccess: a failed READ (network/API error) is NOT an
 * access denial. Signing out on a flaky connection would lock a valid nurse
 * out of the app, so 'retry' wins over everything (the error branch is
 * checked FIRST, even if a row somehow arrived alongside it). Only a CLEAN
 * response with no row, or an inactive row, is a real denial.
 * @param {any} error supabase read error (null/undefined when the read worked)
 * @param {any} row the app_users row (null when none matched)
 * @returns {'retry'|'deny'|'ok'}
 */
export function classifyAccess(error, row) {
  if (error) return 'retry';
  if (!row || !row.is_active) return 'deny';
  return 'ok';
}

// ─── Guaranteed local teardown ─────────────────────────────────────────────

/**
 * Remove every supabase auth key (sb-*) from storage. Exact port of the
 * legacy fallback sweep: never throws (storage may be unavailable), removes
 * nothing else.
 * @param {any} storage localStorage-like (Object.keys()-enumerable, removeItem)
 * @returns {void}
 */
export function sweepAuthKeys(storage) {
  try {
    Object.keys(storage)
      .filter((k) => k.startsWith('sb-'))
      .forEach((k) => storage.removeItem(k));
  } catch {
    /* storage unavailable — nothing to sweep */
  }
}

// A hung signOut (captive portal, dead Wi-Fi mid-request) must not stall the
// teardown: the idle auto-logout claims "Signed out due to inactivity" and
// then reloads — patient data would stay on the unattended screen for the
// browser's full network timeout otherwise.
export const SIGNOUT_TIMEOUT_MS = 3000;

/**
 * End the Supabase session with a GUARANTEED local teardown: try the network
 * signOut, and if it errors, throws, OR fails to settle within timeoutMs,
 * fall back to sweeping the sb-* keys so the browser never keeps a token the
 * server refused to revoke (legacy handleAccess denial branch / logoutApp
 * catch). Never throws.
 * @param {any} client supabase client (defaults to the app client)
 * @param {any} [storage] defaults to globalThis.localStorage
 * @param {number} [timeoutMs] signOut settle deadline before the sweep runs
 * @returns {Promise<void>}
 */
export async function teardownSession(client, storage, timeoutMs = SIGNOUT_TIMEOUT_MS) {
  const st = storage || /** @type {any} */ (globalThis).localStorage;
  /** @type {any} */
  let timer = null;
  try {
    const res = await Promise.race([
      client.auth.signOut(),
      new Promise((resolve) => {
        timer = setTimeout(() => resolve({ error: new Error('signOut timed out') }), timeoutMs);
      }),
    ]);
    if (res && res.error) throw res.error;
  } catch {
    sweepAuthKeys(st);
  } finally {
    clearTimeout(timer);
  }
}

// ─── The access gate (legacy handleAccess) ─────────────────────────────────

// Retry stash: the auth user whose app_users verification read failed. Kept so
// "Tap to retry" can re-verify WITHOUT re-entering the password (the auth
// session is still valid — only the read failed). Legacy _pendingAccessUser.
/** @type {any} */
let _pendingAccessUser = null;

/** @returns {any} the stashed user awaiting a verification retry (or null) */
export function getPendingAccessUser() {
  return _pendingAccessUser;
}

/** Test-only: clear the retry stash. @returns {void} */
export function _resetPendingAccessUser() {
  _pendingAccessUser = null;
}

/**
 * Verify an authenticated user against app_users and settle the session.
 * Port of legacy handleAccess's data half (the login-screen DOM half lives in
 * views/login.js, keyed off the returned status):
 *  - 'retry' — the read failed: stash the user for retryAccess, KEEP the
 *    session (never sign out, never claim denial);
 *  - 'deny'  — clean no-row / inactive: sign out with guaranteed sb-* sweep;
 *    the stash is left as-is (legacy only clears it on success);
 *  - 'ok'    — set the session slice {user, role, fullName} (profile attach:
 *    full_name lives on app_users, not the auth user — F-AUTH-05).
 * @param {any} user supabase auth user
 * @param {{client?: any, storage?: any}} [deps] test injection
 * @returns {Promise<{status: 'retry'|'deny'|'ok', row?: any}>}
 */
export async function verifyAccess(user, deps) {
  const client = (deps && deps.client) || getClient();
  const { data, error } = await client
    .from(TABLES.APP_USERS)
    .select('*')
    .eq('user_id', user.id)
    .maybeSingle();
  const status = classifyAccess(error, data);
  if (status === 'retry') {
    _pendingAccessUser = user;
    return { status };
  }
  if (status === 'deny') {
    await teardownSession(client, deps && deps.storage);
    return { status };
  }
  _pendingAccessUser = null; // clear the retry stash now the verify has passed
  store.set(SLICES.SESSION, { user, role: data.role, fullName: data.full_name });
  return { status, row: data };
}

// ─── Logout (legacy logoutApp) ─────────────────────────────────────────────

/**
 * Sign out with the same teardown guarantee as the denial branch, then reload
 * — the reload lands on the login screen and drops all in-memory state.
 * @param {{client?: any, storage?: any, win?: any}} [deps] test injection
 * @returns {Promise<void>}
 */
export async function logoutApp(deps) {
  const w = (deps && deps.win) || /** @type {any} */ (globalThis);
  await teardownSession((deps && deps.client) || getClient(), deps && deps.storage);
  w.location.reload();
}

// ─── Idle auto-logout (PII safety on shared clinic terminals) ──────────────
// A left-open terminal stays authenticated forever otherwise. After
// IDLE_LOGOUT_MIN minutes with NO user activity we sign the staff member out
// via the normal logout path. Activity resets it, so someone actively typing
// is never logged out mid-task.

export const IDLE_LOGOUT_MIN = 30; // minutes of inactivity before auto-logout
export const IDLE_WARN_SEC = 60; // seconds of warning before that
// High-frequency events (mousemove/scroll/wheel/touch) reset at most once per
// this window so we don't thrash the timers (legacy startIdleTimer throttle).
export const IDLE_THROTTLE_MS = 5000;
/** The assertive lead-in of the warning banner (exported so a test can assert
 *  it is written only after the banner is exposed to assistive tech). */
export const IDLE_WARN_LEAD = 'You will be signed out due to inactivity in';

/**
 * Pure: given "now", when does the warning appear and when does the logout
 * fire? (warnAt = logoutAt − IDLE_WARN_SEC, exactly as legacy resetIdleTimer
 * computed its two setTimeout delays.)
 * @param {number} now epoch ms of the activity that (re)starts the countdown
 * @param {number} [logoutMin]
 * @param {number} [warnSec]
 * @returns {{warnAt: number, logoutAt: number}}
 */
export function nextDeadline(now, logoutMin = IDLE_LOGOUT_MIN, warnSec = IDLE_WARN_SEC) {
  const ms = logoutMin * 60 * 1000;
  return { warnAt: now + ms - warnSec * 1000, logoutAt: now + ms };
}

/**
 * Pure: should the warning banner be showing at `now`? (In the window
 * [warnAt, logoutAt) — at logoutAt the logout itself fires.)
 * @param {number} now
 * @param {{warnAt: number, logoutAt: number}} deadline
 * @returns {boolean}
 */
export function shouldWarn(now, deadline) {
  return now >= deadline.warnAt && now < deadline.logoutAt;
}

/**
 * Pure: throttle gate for high-frequency activity events. Strictly-greater
 * comparison, as legacy (`now-lastReset>5000`).
 * @param {number} lastReset epoch ms of the last throttled reset
 * @param {number} now
 * @returns {boolean} true when this event may reset the countdown
 */
export function shouldResetOnActivity(lastReset, now) {
  return now - lastReset > IDLE_THROTTLE_MS;
}

/** @type {any} */
let idleTimer = null;
/** @type {any} */
let idleWarnTimer = null;
/** @type {any} */
let idleCountdownTimer = null;
let idleWired = false;

/**
 * Find or build the persistent warning banner (legacy #idle-warn markup):
 * the static lead-in lives in an assertive region (announced ONCE when the
 * warning appears); the per-second countdown lives in an aria-live="off"
 * span so SR users aren't interrupted every second.
 * @param {any} doc
 * @returns {any}
 */
function ensureIdleHost(doc) {
  let b = doc.getElementById('idle-warn');
  if (!b) {
    b = doc.createElement('div');
    b.id = 'idle-warn';
    b.className = 'idle-warn';
    // Deliberately NO dialog role: this is a non-modal banner and the
    // assertive span below already announces it; role="alertdialog" would
    // demand an accessible name + focus move it never gets. Hidden state is
    // aria-hidden + CSS visibility:hidden (the visibility toggle is what
    // removes the button from the tab order — see .idle-warn in app.css).
    b.setAttribute('aria-hidden', 'true');
    b.innerHTML =
      '<span><span id="idle-warn-msg" aria-live="assertive"></span> ' +
      '<span id="idle-warn-count" aria-live="off"></span></span>' +
      '<button type="button" class="btn sm" data-action="idle-stay">Stay signed in</button>';
    doc.body.appendChild(b);
  }
  return b;
}

/**
 * Show the persistent inactivity warning with a live 60s countdown. A fixed
 * banner that stays visible until activity resets the timer or the final
 * logout fires (replaced the old single 3s toast that could scroll away
 * unseen). Exact legacy port.
 * @returns {void}
 */
export function showIdleWarning() {
  const doc = /** @type {any} */ (globalThis).document;
  if (!doc) return;
  const b = ensureIdleHost(doc);
  const m = doc.getElementById('idle-warn-msg');
  const c = doc.getElementById('idle-warn-count');
  if (!b || !m || !c) return;
  let secs = IDLE_WARN_SEC;
  const render = () => {
    c.textContent = `${secs}s.`;
  };
  // REVEAL FIRST, THEN SPEAK — with a real gap between the two. A live region
  // only announces content that changes while it is in the accessibility tree.
  // This used to write the assertive lead-in and THEN flip .show /
  // aria-hidden="false" — at mutation time the banner was visibility:hidden
  // AND aria-hidden, so the one announcement that matters ("you are about to
  // be signed out") was never made to a screen-reader user.
  //
  // Reordering alone did not fix it: both statements ran in ONE synchronous
  // block, so the accessibility pass still met the banner with its text
  // already in place. announce() empties the region now and writes it after
  // two frames — see ui/announce.js for why one is not enough.
  b.classList.add('show');
  b.setAttribute('aria-hidden', 'false');
  announce(m, IDLE_WARN_LEAD);
  // The countdown is aria-live="off" and is what the banner READS as, so it
  // stays synchronous: the banner must never paint without its seconds.
  render();
  clearInterval(idleCountdownTimer);
  idleCountdownTimer = setInterval(() => {
    secs--;
    if (secs <= 0) {
      clearInterval(idleCountdownTimer);
      return; // the logout timeout fires the actual sign-out
    }
    render();
  }, 1000);
}

/** Hide the warning banner and stop its countdown. @returns {void} */
export function hideIdleWarning() {
  clearInterval(idleCountdownTimer);
  const doc = /** @type {any} */ (globalThis).document;
  if (!doc) return;
  const b = doc.getElementById('idle-warn');
  const m = doc.getElementById('idle-warn-msg');
  const c = doc.getElementById('idle-warn-count');
  if (b) {
    b.classList.remove('show');
    b.setAttribute('aria-hidden', 'true');
  }
  // Clear so the next warning re-announces the assertive lead-in from empty,
  // and drop any deferred write still in flight — otherwise an announcement
  // scheduled a frame ago repopulates a banner that is already hidden.
  if (m) {
    cancelAnnounce(m);
    m.textContent = '';
  }
  if (c) c.textContent = '';
}

/**
 * Reset the inactivity countdown. Runs on every user activity event and once
 * at login. Only arms while a staff user is signed in (session slice set) —
 * never on the login screen. Exact legacy port over nextDeadline().
 * @returns {void}
 */
export function resetIdleTimer() {
  const sess = store.get(SLICES.SESSION);
  if (!sess || !sess.user) return;
  clearTimeout(idleTimer);
  clearTimeout(idleWarnTimer);
  hideIdleWarning();
  const now = Date.now();
  const d = nextDeadline(now);
  idleWarnTimer = setTimeout(showIdleWarning, d.warnAt - now);
  idleTimer = setTimeout(() => {
    hideIdleWarning();
    toast('Signed out due to inactivity', 'err');
    logoutApp();
  }, d.logoutAt - now);
}

/**
 * Wire activity listeners once and start the countdown. Called after a
 * successful sign-in. Discrete events (pointerdown/keydown) reset directly;
 * high-frequency events (mousemove/wheel/touchstart/scroll) are throttled to
 * at most once per IDLE_THROTTLE_MS so we don't thrash the timers — this
 * keeps the on-screen "move the mouse / press a key" promise honest and stops
 * a staff member who is only reading/scrolling a record being logged out.
 * Tab-becomes-visible also resets. Exact legacy port.
 * @returns {void}
 */
export function startIdleTimer() {
  const doc = /** @type {any} */ (globalThis).document;
  if (doc && !idleWired) {
    idleWired = true;
    register('idle-stay', () => resetIdleTimer());
    let lastReset = 0;
    const onActivity = () => {
      const now = Date.now();
      if (shouldResetOnActivity(lastReset, now)) {
        lastReset = now;
        resetIdleTimer();
      }
    };
    ['pointerdown', 'keydown'].forEach((ev) =>
      doc.addEventListener(ev, resetIdleTimer, { passive: true })
    );
    ['mousemove', 'wheel', 'touchstart'].forEach((ev) =>
      doc.addEventListener(ev, onActivity, { passive: true })
    );
    doc.addEventListener('scroll', onActivity, { passive: true, capture: true });
    doc.addEventListener('visibilitychange', () => {
      if (!doc.hidden) resetIdleTimer();
    });
  }
  resetIdleTimer();
}
